import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { ensureNotNil } from "client/Misc/utils";
import { UserDetailsBox } from "client/Features/Users/UserDetailsBox/UserDetailsBox";
import { useContactDialog } from "client/Features/AddressBook/ContactDialog";
import { ContactSource, getWorkspaceContactIdFromUserId } from "client/Api/Email/contacts";
import styles from "./HubUserInfoPopOver.module.less";
import { useDelayedEvent, useIsTouchDevice } from "client/Hooks";
import { Anchor, DropOverAlignmentValue } from "client/Components";

export type HubUserInfoPopOverProps = {
	userIdentity: UserIdentityModel;
	shouldBubbleClick?: boolean;
	targetAlignment?: DropOverAlignmentValue;
	contentAlignment?: DropOverAlignmentValue;
	trackingId: string;
};

export const HubUserInfoPopOver = (props: PropsWithChildren<HubUserInfoPopOverProps>): ReactElement => {
	const {
		userIdentity,
		shouldBubbleClick = true,
		targetAlignment,
		contentAlignment,
		children,
		trackingId
	} = props;

	const [isDetailsDropOverOpen, setIsDetailsDropOverOpen] = useState<boolean>(false);

	const dropOverTargetRef: RefObject<HTMLAnchorElement | null> = useRef<HTMLAnchorElement>(null);

	const [contactDialog, openContactDialog] = useContactDialog();
	const isTouchDevice: boolean = useIsTouchDevice();

	const moreInfoHandler = useCallback(
		(): void => {
			openContactDialog({
				source: ContactSource.Workspace,
				initialView: {
					kind: "details",
					contactId: getWorkspaceContactIdFromUserId(userIdentity.userId)
				}
			});
		},
		[
			userIdentity.userId,
			openContactDialog
		]
	);

	const showDetailsHandler = useCallback(
		(): void => {
			if (!isTouchDevice) {
				setIsDetailsDropOverOpen(true);
			}
		},
		[isTouchDevice]
	);

	const hideDetailsHandler = useCallback(
		(): void => setIsDetailsDropOverOpen(false),
		[]
	);

	const [triggerMouseEnter, cancelMouseEnter] = useDelayedEvent(
		showDetailsHandler,
		300
	);

	const [triggerMouseLeave, cancelMouseLeave] = useDelayedEvent(
		hideDetailsHandler,
		100
	);

	const mouseEnterHandler = useCallback(
		(): void => {
			triggerMouseEnter();
			cancelMouseLeave();
		},
		[cancelMouseLeave, triggerMouseEnter]
	);

	const mouseLeaveHandler = useCallback(
		(): void => {
			triggerMouseLeave();
			cancelMouseEnter();
		},
		[cancelMouseEnter, triggerMouseLeave]
	);

	const preventBubblingHandler = useMemo(
		(): ((args: MouseEvent) => void) | undefined => {
			if (shouldBubbleClick) {
				return undefined;
			}

			return (args: MouseEvent): void => {
				args.preventDefault();
				args.stopPropagation();
			};
		},
		[shouldBubbleClick]
	);

	return (
		<>
			<span
				onMouseEnter={mouseEnterHandler}
				onMouseLeave={mouseLeaveHandler}
				onClick={preventBubblingHandler}
			>
				<Anchor
					ref={dropOverTargetRef}
					trackingId={trackingId}
					className={styles.anchor}
					onClick={moreInfoHandler}
				>
					{children}
				</Anchor>

				{isDetailsDropOverOpen
					&& !isTouchDevice
					&& (
						<UserDetailsBox
							trackingId="hub-avatar__drop-over"
							target={ensureNotNil(dropOverTargetRef.current)}
							userInfo={{
								kind: "userIdentity",
								userIdentity
							}}
							onClose={hideDetailsHandler}
							onMoreInfo={moreInfoHandler}
							targetAlignment={targetAlignment}
							contentAlignment={contentAlignment}
						/>
					)
				}
			</span>
			{contactDialog}
		</>
	);
};

HubUserInfoPopOver.displayName = "HubUserInfoPopOver";